import { GET_SESSION_BY_ID, type QuerySessionResponse } from '../../../../../contracts';
import { getSessionDataConfig } from '../../../../config';
import {
  appendDesktopChunk,
  applyRequestedAttributes,
  beginStreamingAttempt,
  failBeforeStream,
  finishStreamingWithFallback,
  keepStreamedTextOnStop,
  markFinishingStreaming,
  markUploadsFinished,
  publishGenerationController,
  releaseToNotStarted,
  resetResultForOpen,
  restorePreviousNoteOnStop,
  settleAfterClose,
  type BaselineRestore,
  type CloseFirstWrite,
  type RequestedNoteAttributes,
  type StopRestore,
  type StreamStart,
} from './session-cache-reducers';

export type SessionCacheWrite = {
  previous: QuerySessionResponse;
  next: QuerySessionResponse;
};

// Lifecycle coordination only: each named effect publishes one synchronous write.
export function writeSessionCache(
  sessionId: string,
  reduce: (old: QuerySessionResponse) => QuerySessionResponse
): SessionCacheWrite | undefined {
  let write: SessionCacheWrite | undefined;
  getSessionDataConfig()
    .getQueryClient()
    .setQueryData<QuerySessionResponse>([GET_SESSION_BY_ID, { sessionId }], (oldData) => {
      if (!oldData) return undefined;
      const next = reduce(oldData);
      write = { previous: oldData, next };
      return next;
    });
  return write;
}

export const writeRequestedAttributes = (
  sessionId: string,
  attrs: RequestedNoteAttributes
) => writeSessionCache(sessionId, (old) => applyRequestedAttributes(old, attrs));
export const writeGenerationRegistered = (
  sessionId: string,
  controller: AbortController
) => writeSessionCache(sessionId, (old) => publishGenerationController(old, controller));
export const writePersonalisationFinishing = (sessionId: string) =>
  writeSessionCache(sessionId, markFinishingStreaming);
export const writeStopRestoredPrevious = (sessionId: string, restore: StopRestore) =>
  writeSessionCache(sessionId, (old) => restorePreviousNoteOnStop(old, restore));
export const writeStopKeptStreamedText = (sessionId: string, text: string) =>
  writeSessionCache(sessionId, (old) => keepStreamedTextOnStop(old, text));
export const writePreStreamFailure = (sessionId: string, restore: BaselineRestore) =>
  writeSessionCache(sessionId, (old) => failBeforeStream(old, restore));
// The sole producer seam retains site 7's lazy, ordered flag reads after the cache guard.
export const writeStreamStarting = (sessionId: string, read: () => StreamStart) =>
  writeSessionCache(sessionId, (old) => beginStreamingAttempt(old, read()));
export const writeUploadsFinished = (sessionId: string) =>
  writeSessionCache(sessionId, markUploadsFinished);
export const writeReleased = (sessionId: string) =>
  writeSessionCache(sessionId, releaseToNotStarted);
export const writeOpened = (sessionId: string) =>
  writeSessionCache(sessionId, resetResultForOpen);

export function writeDesktopChunk(
  sessionId: string,
  chunk: string,
  isJsonTemplate: boolean
): SessionCacheWrite | undefined {
  const cached = getSessionDataConfig()
    .getQueryClient()
    .getQueryData<QuerySessionResponse>([GET_SESSION_BY_ID, { sessionId }]);
  const newText = (cached?.consult_note.result || '') + chunk;
  return writeSessionCache(sessionId, (old) =>
    appendDesktopChunk(old, newText, isJsonTemplate)
  );
}

export const writeCloseStarted = (sessionId: string, close: CloseFirstWrite) =>
  writeSessionCache(sessionId, (old) => finishStreamingWithFallback(old, close));
export const writeCloseSettled = (
  sessionId: string,
  previousResult: string | null | undefined
) => writeSessionCache(sessionId, (old) => settleAfterClose(old, previousResult));
